import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Eye, Heart, MapPin, Calendar, ArrowRight, ChevronLeft, ChevronRight, Edit, Trash2, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { pb } from '@/lib/pocketbase';
import TravelStoryDetailModal from './TravelStoryDetailModal';
import { useToast } from '@/hooks/use-toast';
import { getProfilePictureUrl } from '@/utils/profileUtils';
import { getOptimizedStoryThumbnailUrl, getOptimizedAvatarUrl } from '@/utils/imageOptimization';
interface TravelStory {
  id: string;
  title: string;
  caption: string;
  location?: string;
  images: string[];
  videos?: string[];
  tutorId: string;
  tutorName: string;
  tutorAvatar?: string;
  likes: number;
  views: number;
  created: string;
}

interface HomeTravelStoriesProps {
  className?: string;
}

const HomeTravelStories: React.FC<HomeTravelStoriesProps> = ({ className = '' }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stories, setStories] = useState<TravelStory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [currentTutorProfile, setCurrentTutorProfile] = useState<any>(null);
  const [desktopSlide, setDesktopSlide] = useState(0);
  const [selectedStory, setSelectedStory] = useState<TravelStory | null>(null);
  const [selectedStoryIndex, setSelectedStoryIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  useEffect(() => {
    if (!hasFetched) {
      fetchTravelStories();
    }
    if (user?.role === 'tutor') {
      fetchTutorProfile();
    }
  }, [user, hasFetched]);

  // Track window width for responsive calculations
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      // Reset slide position when window resizes to avoid being stuck in invalid positions
      setDesktopSlide(0);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchTutorProfile = async () => {
    if (!user?.id) return;
    
    try {
      const tutorProfiles = await pb.collection('tutors').getList(1, 1, {
        filter: `user = "${user.id}"`
      });
      
      if (tutorProfiles.items.length > 0) {
        setCurrentTutorProfile(tutorProfiles.items[0]);
      }
    } catch (error) {
      console.error('Error fetching tutor profile:', error);
    }
  };

  const fetchTravelStories = async () => {
    setIsLoading(true);
    try {
      // Fetch more stories to ensure we get all ordered ones
      const fetchCount = 50; // Fetch enough to get all ordered stories
      
      const storiesData = await pb.collection('travel_stories').getList(1, fetchCount, {
        sort: '-display_order,-created',
        filter: 'isPublished = true',
        expand: 'tutorId'
      });

      console.log('📚 Raw stories from DB (total fetched):', storiesData.items.length);
      storiesData.items.forEach((s, i) => {
        console.log(`Story ${i + 1}: "${s.title}" - display_order: ${s.display_order || 0}, created: ${s.created}`);
      });

      // Already sorted DESC by PocketBase, take only what we need
      const displayCount = windowWidth >= 1024 ? 12 : 8;
      const limitedItems = storiesData.items.slice(0, displayCount);

      const formattedStories: TravelStory[] = limitedItems.map((story: any) => {
        return {
          id: story.id,
          title: story.title,
          caption: story.caption,
          location: story.location,
          images: Array.isArray(story.images) ? story.images : [story.images].filter(Boolean),
          videos: Array.isArray(story.videos) ? story.videos : (story.videos ? [story.videos] : []),
          tutorId: story.tutorId,
          tutorName: story.expand?.tutorId?.name || story.tutorName || 'Anonymous',
          tutorAvatar: story.expand?.tutorId?.profilePicture,
          likes: 0,
          views: 0,
          created: story.created
        };
      });

      console.log('✅ Final sorted stories for display:');
      formattedStories.forEach((s, i) => {
        console.log(`Position ${i + 1}: "${s.title}"`);
      });

      setStories(formattedStories);
      setHasFetched(true);
    } catch (error) {
      console.error('Error fetching travel stories:', error);
    }
    setIsLoading(false);
  };

  const getImageUrl = (story: TravelStory) => {
    if (story.images && story.images.length > 0) {
      const firstImage = story.images[0];
      if (firstImage.startsWith('http')) {
        return firstImage;
      }
      try {
        // Use optimized thumbnail (168x224) for story cards
        return getOptimizedStoryThumbnailUrl({ collectionId: 'travel_stories', id: story.id }, firstImage);
      } catch {
        return `https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=300&fit=crop`;
      }
    }
    return `https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=400&h=300&fit=crop`;
  };

  const getVideoUrl = (story: TravelStory) => {
    if (story.videos && story.videos.length > 0) {
      const firstVideo = story.videos[0];
      if (firstVideo.startsWith('http')) {
        return firstVideo;
      }
      try {
        return pb.files.getURL({ collectionId: 'travel_stories', id: story.id }, firstVideo);
      } catch {
        return null;
      }
    }
    return null;
  };

  const hasVideo = (story: TravelStory) => {
    return story.videos && story.videos.length > 0;
  };

  const getTutorProfileUrl = (story: TravelStory) => {
    if (story.tutorAvatar) {
      try {
        // Use optimized avatar (48x48) for tutor profiles in story cards
        return getOptimizedAvatarUrl({ collectionId: 'tutors', id: story.tutorId }, story.tutorAvatar, 48);
      } catch {
        return null;
      }
    }
    return null;
  };

  const handleViewAllStories = () => {
    navigate('/travel-stories');
  };

  const handleCreateStory = () => {
    // Check if tutor has a profile picture
    if (!currentTutorProfile || !getProfilePictureUrl(currentTutorProfile)) {
      toast({
        title: "Complete your profile first",
        description: "You need to complete your profile before creating travel stories.",
        variant: "destructive"
      });
      navigate('/profile');
      return;
    }
    navigate('/my-travel-stories');
  };

  const handleViewStory = (storyId: string) => {
    console.log('handleViewStory called with storyId:', storyId);
    console.log('Available stories:', displayStories);
    
    // Handle View All special case
    if (storyId === 'view-all') {
      handleViewAllStories();
      return;
    }
    
    const storyIndex = displayStories.findIndex(s => s.id === storyId);
    console.log('Found story index:', storyIndex);
    if (storyIndex !== -1) {
      const story = displayStories[storyIndex];
      console.log('Setting selected story:', story);
      setSelectedStory(story);
      setSelectedStoryIndex(storyIndex);
      setIsModalOpen(true);
    }
  };

  const handleNavigateStory = (index: number) => {
    console.log('handleNavigateStory called with index:', index);
    console.log('Available modal stories for navigation:', modalStories);
    
    if (index >= 0 && index < modalStories.length) {
      const story = modalStories[index];
      console.log('Navigating to story:', story);
      
      // Just set the story - don't automatically navigate away for View All
      setSelectedStory(story);
      setSelectedStoryIndex(index);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays}d ago`;
    }
  };

  const handleEditStory = (story: TravelStory) => {
    navigate(`/travel-story/edit/${story.id}`);
  };

  const handleDeleteStory = async (storyId: string) => {
    if (confirm('Are you sure you want to delete this story?')) {
      try {
        await pb.collection('travel_stories').delete(storyId);
        setStories(prev => prev.filter(s => s.id !== storyId));
        toast({
          title: "Story deleted",
          description: "Your travel story has been deleted successfully."
        });
      } catch (error) {
        console.error('Error deleting story:', error);
        toast({
          title: "Error",
          description: "Failed to delete the story. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const isStoryCreator = (story: TravelStory) => {
    return user && currentTutorProfile && story.tutorId === currentTutorProfile.id;
  };

  // Prepare display stories with View All option - show all fetched stories
  const displayStories = stories;
  
  // Create a virtual "View All" story for navigation
  const viewAllStory: TravelStory = {
    id: 'view-all',
    title: 'View All Stories',
    caption: 'Browse all travel stories from our tutors around the world',
    location: 'Global',
    images: ['https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop'],
    tutorId: 'system',
    tutorName: 'TutorNesta',
    likes: 0,
    views: 0,
    created: new Date().toISOString()
  };
  
  // Stories for modal navigation (includes View All at the end)
  const modalStories = [...displayStories, viewAllStory];
  
  // Dynamic calculation based on screen width
  const hasProfilePicture = currentTutorProfile && getProfilePictureUrl(currentTutorProfile);
  const createStoryCardCount = (user?.role === 'tutor' && currentTutorProfile && hasProfilePicture) ? 1 : 0;
  const totalCards = displayStories.length + createStoryCardCount + 1; // +1 for View All card
  
  // Calculate visible cards based on actual screen width
  const calculateVisibleCards = () => {
    // Account for sidebar width (approximately 256px) and margins/paddings
    const sidebarWidth = windowWidth >= 1024 ? 256 : 0; // Sidebar visible on lg+ screens
    const availableWidth = windowWidth - sidebarWidth - 64 - 32; // Subtract margins and paddings
    const cardWidth = 112; // Card width in pixels (w-28)
    const gapWidth = 8; // Gap between cards (gap-2)
    
    const calculated = Math.floor(availableWidth / (cardWidth + gapWidth));
    // Ensure we never show all cards at once - always leave room for scrolling
    return Math.min(calculated, Math.max(6, totalCards - 2)); // Show max 6 cards or total-2, whichever is smaller
  };
  
  const desktopVisibleCards = Math.max(4, calculateVisibleCards()); // Minimum 4 cards
  
  // Desktop: Ensure scrolling is always possible if we have more than minimum cards
  const maxDesktopSlides = totalCards > desktopVisibleCards ? totalCards - desktopVisibleCards : 0;

  // Desktop carousel functions
  const nextDesktopSlide = () => {
    if (maxDesktopSlides > 0) {
      setDesktopSlide((prev) => Math.min(prev + 1, maxDesktopSlides));
    }
  };

  const prevDesktopSlide = () => {
    setDesktopSlide((prev) => Math.max(prev - 1, 0));
  };

  if (isLoading) {
    return (
      <section className={`py-4 cls-stable-stories ${className}`} style={{ minHeight: '228px', contain: 'layout style paint', contentVisibility: 'auto', containIntrinsicSize: 'auto 228px' }}>
        <div className="px-0" style={{ height: '200px' }}>
          <div className="flex gap-3 overflow-x-auto" style={{ height: '190px' }}>
            {[...Array(6)].map((_, index) => (
              <Card key={index} className="animate-pulse flex-shrink-0 w-32 h-40">
                <div className="w-full h-full bg-gray-200 rounded-lg"></div>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-1 cls-stable-stories ${className}`} style={{ minHeight: '228px', contain: 'layout style paint', contentVisibility: 'auto', containIntrinsicSize: 'auto 228px' }}>
      <div className="px-0" style={{ minHeight: '200px' }}>
        {/* Section Title */}
        <div className="px-4 md:px-12 lg:px-16 mb-2">
          <h2 className="text-sm font-medium text-muted-foreground">
            Tutors Stories
          </h2>
        </div>
        
        {/* Stories Grid */}
        <div className="relative">
          
          {/* Mobile: Horizontal scrolling with finger swipe */}
          <div className="md:hidden">
            <div className="w-full overflow-hidden px-1">
              <div 
                className="flex gap-1.5 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden snap-x snap-mandatory"
                style={{ 
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {/* Create Story Card for Tutor */}
                {user?.role === 'tutor' && currentTutorProfile && hasProfilePicture && (
                  <Card className="flex-shrink-0 w-28 h-48 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden snap-start">
                    <div className="relative w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex flex-col items-center justify-center p-2">
                      {getTutorProfileUrl({ tutorAvatar: currentTutorProfile.profilePicture, tutorId: currentTutorProfile.id } as TravelStory) ? (
                        <img
                          src={getTutorProfileUrl({ tutorAvatar: currentTutorProfile.profilePicture, tutorId: currentTutorProfile.id } as TravelStory)!}
                          alt="Your Profile"
                          className="w-6 h-6 rounded-full object-cover mb-1"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mb-1">
                          <span className="text-white text-xs font-bold">
                            {currentTutorProfile.name?.charAt(0) || user.name?.charAt(0) || 'T'}
                          </span>
                        </div>
                      )}
                      <Button 
                        onClick={handleCreateStory}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 h-5 w-5 p-0"
                      >
                        <Plus className="h-2 w-2" />
                      </Button>
                    </div>
                  </Card>
                )}

                 {/* Story Cards */}
                {displayStories.map((story, index) => {
                  return (
                    <Card 
                      key={story.id} 
                      className="flex-shrink-0 w-28 h-48 cursor-pointer border border-gray-200 overflow-hidden snap-start"
                      onClick={() => handleViewStory(story.id)}
                    >
                      <div className="relative w-full h-full">
                        <div className="w-full h-full overflow-hidden">
                          {hasVideo(story) ? (
                            <video
                              src={getVideoUrl(story) || undefined}
                              className="w-full h-full object-cover"
                              autoPlay
                              muted
                              loop
                              playsInline
                            />
                          ) : (
                            <img
                              src={getImageUrl(story)}
                              alt={story.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                          
                          {/* Tutor Avatar */}
                          <div className="absolute top-2 left-2 w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden bg-white z-10">
                            {story.tutorAvatar ? (
                              <img
                                src={getTutorProfileUrl(story)!}
                                alt={story.tutorName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                                <User className="w-4 h-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          
                            {/* Edit/Delete buttons for story creator */}
                            {isStoryCreator(story) && (
                              <div className="absolute top-1 right-1 flex gap-1 z-10">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-white hover:text-white p-1 h-auto bg-blue-600/95 hover:bg-blue-700 backdrop-blur-sm rounded-full border border-white/30 shadow-lg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditStory(story);
                                  }}
                                >
                                  <Edit className="h-2.5 w-2.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-white hover:text-white p-1 h-auto bg-red-600/95 hover:bg-red-700 backdrop-blur-sm rounded-full border border-white/30 shadow-lg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteStory(story.id);
                                  }}
                                >
                                  <Trash2 className="h-2.5 w-2.5" />
                                </Button>
                              </div>
                            )}

                            {/* Tutor Name Overlay */}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-1">
                              <p className="text-white text-[10px] text-center truncate">
                                {story.tutorName}
                              </p>
                            </div>
                           </div>
                        </div>
                    </Card>
                  );
                })}

                {/* View All Stories Card */}
                <Card 
                  className="flex-shrink-0 w-28 h-48 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden snap-start"
                  onClick={handleViewAllStories}
                >
                  <div className="relative w-full h-full bg-gradient-to-br from-gray-100 to-blue-100 flex flex-col items-center justify-center p-2">
                    <ArrowRight className="h-4 w-4 text-blue-600 mb-1" />
                    <span className="text-blue-600 text-xs font-medium text-center">View All</span>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Desktop: Horizontal sliding carousel */}
          <div className="hidden md:block mt-4">
            <div className="relative px-4">
              {/* Left Navigation Arrow */}
              {desktopSlide > 0 && (
                <Button
                  variant="ghost"
                  onClick={prevDesktopSlide}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm hover:bg-white shadow-lg rounded-full h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}

              {/* Right Navigation Arrow */}
              {desktopSlide < maxDesktopSlides && (
                <Button
                  variant="ghost"
                  onClick={nextDesktopSlide}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm hover:bg-white shadow-lg rounded-full h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}

              {/* Stories Container */}
              <div className="overflow-hidden mx-12">
                <div 
                  className="flex gap-2 transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${desktopSlide * 120}px)` }}
                >
                  {/* Create Story Card for Tutor */}
                  {user?.role === 'tutor' && currentTutorProfile && hasProfilePicture && (
                    <Card className="flex-shrink-0 w-28 h-44 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden">
                      <div className="relative w-full h-full bg-gradient-to-br from-blue-100 to-purple-100 flex flex-col items-center justify-center p-3">
                        {getTutorProfileUrl({ tutorAvatar: currentTutorProfile.profilePicture, tutorId: currentTutorProfile.id } as TravelStory) ? (
                          <img
                            src={getTutorProfileUrl({ tutorAvatar: currentTutorProfile.profilePicture, tutorId: currentTutorProfile.id } as TravelStory)!}
                            alt="Your Profile"
                            className="w-8 h-8 rounded-full object-cover mb-2"
                          />
                        ) : (
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mb-2">
                            <span className="text-white text-sm font-bold">
                              {currentTutorProfile.name?.charAt(0) || user.name?.charAt(0) || 'T'}
                            </span>
                          </div>
                        )}
                        <Button 
                          onClick={handleCreateStory}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 h-6 w-6 p-0"
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </Card>
                  )}

                  {/* Story Cards */}
                  {displayStories.map((story, index) => {
                    return (
                      <Card 
                        key={story.id} 
                        className="flex-shrink-0 w-28 h-44 cursor-pointer border border-gray-200 overflow-hidden"
                        onClick={() => handleViewStory(story.id)}
                      >
                        <div className="relative w-full h-full">
                          <div className="w-full h-full overflow-hidden">
                            {hasVideo(story) ? (
                              <video
                                src={getVideoUrl(story) || undefined}
                                className="w-full h-full object-cover"
                                autoPlay
                                muted
                                loop
                                playsInline
                              />
                            ) : (
                              <img
                                src={getImageUrl(story)}
                                alt={story.title}
                                className="w-full h-full object-cover"
                              />
                            )}
                            
                            {/* Tutor Avatar */}
                            <div className="absolute top-2 left-2 w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden bg-white z-10">
                              {story.tutorAvatar ? (
                                <img
                                  src={getTutorProfileUrl(story)!}
                                  alt={story.tutorName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                                  <User className="w-4 h-4 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            
                           {/* Edit/Delete buttons for story creator */}
                           {isStoryCreator(story) && (
                             <div className="absolute top-1 right-1 flex gap-1 z-10">
                               <Button
                                 variant="ghost"
                                 size="sm"
                                 className="text-white hover:text-white p-1 h-auto bg-blue-600/95 hover:bg-blue-700 backdrop-blur-sm rounded-full border border-white/30 shadow-lg"
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   handleEditStory(story);
                                 }}
                               >
                                 <Edit className="h-2.5 w-2.5" />
                               </Button>
                               <Button
                                 variant="ghost"
                                 size="sm"
                                 className="text-white hover:text-white p-1 h-auto bg-red-600/95 hover:bg-red-700 backdrop-blur-sm rounded-full border border-white/30 shadow-lg"
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   handleDeleteStory(story.id);
                                 }}
                               >
                                 <Trash2 className="h-2.5 w-2.5" />
                               </Button>
                             </div>
                           )}

                           {/* Tutor Name Overlay */}
                           <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-1.5">
                             <p className="text-white text-[11px] text-center truncate">
                               {story.tutorName}
                             </p>
                           </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}

                  {/* View All Stories Card - always last */}
                  <Card 
                    className="flex-shrink-0 w-28 h-44 cursor-pointer hover:shadow-lg transition-shadow overflow-hidden"
                    onClick={handleViewAllStories}
                  >
                    <div className="relative w-full h-full bg-gradient-to-br from-gray-100 to-blue-100 flex flex-col items-center justify-center p-3">
                      <ArrowRight className="h-5 w-5 text-blue-600 mb-2" />
                      <span className="text-blue-600 text-sm font-medium text-center">View All</span>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Story Detail Modal */}
      <TravelStoryDetailModal 
        story={selectedStory}
        stories={modalStories}
        currentIndex={selectedStoryIndex}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedStory(null);
        }}
        onNavigate={handleNavigateStory}
        onViewAll={handleViewAllStories}
      />
    </section>
  );
};

export default HomeTravelStories;
